import { useEffect, useRef, useState } from 'react'
import LinkRoundedIcon from '@mui/icons-material/LinkRounded'
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded'
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded'
import { inspectAiChatLink } from '../../../services/aiChatHistoryService'
import { TextInput } from '../../Common/TextInput'
import { Button } from '../../Common/Button'
import { useNls } from '../../../nls/nlsProvider'
import type { DataContext } from '../../../types/aiChatSource'

export interface PublicLinkPaneProps {
  workspaceId: string
  onAdd: (context: DataContext) => void
}

type InspectState =
  | { status: 'idle' }
  | { status: 'busy' }
  | { status: 'supported'; result: Awaited<ReturnType<typeof inspectAiChatLink>> }
  | { status: 'unsupported'; reason: string }

export const PublicLinkPane = ({ workspaceId, onAdd }: PublicLinkPaneProps) => {
  const { t } = useNls()
  const [url, setUrl] = useState('')
  const [inspectState, setInspectState] = useState<InspectState>({ status: 'idle' })
  const debounceRef = useRef<number | null>(null)

  useEffect(() => {
    const trimmed = url.trim()
    if (!trimmed) {
      setInspectState((prev) => (prev.status === 'idle' ? prev : { status: 'idle' }))
      return
    }
    if (debounceRef.current) window.clearTimeout(debounceRef.current)
    debounceRef.current = window.setTimeout(async () => {
      setInspectState({ status: 'busy' })
      try {
        const result = await inspectAiChatLink(workspaceId, trimmed)
        if (result.supported) {
          setInspectState({ status: 'supported', result })
        } else {
          setInspectState({ status: 'unsupported', reason: result.reason ?? t('NLSAiContextPickerLinkCannotRead') })
        }
      } catch {
        setInspectState({ status: 'unsupported', reason: t('NLSAiContextPickerLinkCannotReach') })
      }
    }, 450)
    return () => { if (debounceRef.current) window.clearTimeout(debounceRef.current) }
  }, [url, workspaceId])

  const handleAttach = () => {
    if (inspectState.status !== 'supported') return
    const context: DataContext = {
      id: `link:${url}`,
      kind: 'link',
      label: inspectState.result.title || url,
      text: inspectState.result.text || '',
      fixedIngestCr: 2,
    }
    onAdd(context)
    setUrl('')
    setInspectState({ status: 'idle' })
  }

  return (
    <div className="ctxPane ctxPane--link">
      <div className="ctxPane__ingestHero">
        <LinkRoundedIcon style={{ fontSize: 36, opacity: 0.5 }} />
        <h4>{t('NLSAiContextPickerCategoryLink')}</h4>
        <p>Paste any public HTTPS web page. Line validates it can read the content.</p>
        <p className="ctxPane__costNote">+2 Cr fixed per link</p>
      </div>

      <div className="ctxPane__urlRow">
        <TextInput
          type="url"
          className="ctxPane__urlInput"
          placeholder="https://example.com/page"
          value={url}
          onChange={setUrl}
          aria-label={t('NLSAiContextPickerCategoryLink')}
        />
      </div>

      {inspectState.status === 'busy' && (
        <div className="ctxPane__parseStatus ctxPane__parseStatus--busy">
          <div className="ctxPane__spinner" />
          <span>{t('NLSAiContextPickerCheckingLink')}</span>
        </div>
      )}

      {inspectState.status === 'supported' && (
        <div className="ctxPane__parseStatus ctxPane__parseStatus--done">
          <CheckCircleRoundedIcon style={{ color: 'var(--success)' }} />
          <div className="ctxPane__parseInfo">
            <strong>{t('NLSAiContextPickerLinkSupported')}</strong>
            {inspectState.result.title && <small>{inspectState.result.title}</small>}
          </div>
          <Button size="sm" variant="primary" onClick={handleAttach}>
            {t('NLSAiContextPickerAttach')}
          </Button>
        </div>
      )}

      {inspectState.status === 'unsupported' && (
        <div className="ctxPane__parseStatus ctxPane__parseStatus--error">
          <ErrorOutlineRoundedIcon style={{ color: 'var(--danger)' }} />
          <div className="ctxPane__parseInfo">
            <strong>{t('NLSAiContextPickerLinkNotSupported')}</strong>
            <small>{inspectState.reason}</small>
          </div>
        </div>
      )}
    </div>
  )
}
